import { omitBy } from "lodash-es";
import { useCallback, useRef, useState, type RefObject } from "react";
import {
  type EditorRef,
  type EmailEditorProps as UnlayerEmailEditorProps,
} from "react-email-editor";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { downloadFile } from "../../../utils/download-files";
import { uuidv4 } from "../../../utils/uuid";
import { useAnalytics } from "../../analytics/analytics";
import { useGetImageAssets } from "../../assets/hooks/use-get-image-assets";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { SURVEY_ANSWERS_CONTENT_SLUG } from "../custom-tools/survey-answers-custom-tool";
import type { UnlayerDesign } from "../types/unlayer-design";
import type { UnlayerUpload } from "../types/unlayer-upload";
import { fixUnlayerHtmlEntities } from "../utils/fix-unlayer-html-entities";
import { injectSurveyAnswerLoop } from "../utils/inject-survey-answer-loop";
import { prepareUnlayerPollDesign } from "../utils/unlayer-poll-blocks";
import { useDisplayCondition, type DisplayConditionReturn } from "./use-display-condition";
import { useUploadImage } from "./use-upload-image";

const isSurveyAnswerContent = (content: { type?: unknown; slug?: unknown }): boolean =>
  (content.type === "custom" && content.slug === SURVEY_ANSWERS_CONTENT_SLUG) ||
  content.type === `custom#${SURVEY_ANSWERS_CONTENT_SLUG}` ||
  content.type === SURVEY_ANSWERS_CONTENT_SLUG;

type UnlayerPlainTextEditor = NonNullable<EditorRef["editor"]> & {
  exportPlainText: (cb: (data: { design: UnlayerDesign; text: string }) => void) => void;
};

export function stripSurveyAnswerBlocks(design: UnlayerDesign): UnlayerDesign {
  return {
    ...design,
    body: {
      ...design.body,
      rows: design.body.rows
        .map((row) => {
          const containedSurveyAnswers = row.columns.some((column) =>
            column.contents.some(isSurveyAnswerContent),
          );
          const strippedRow = {
            ...row,
            columns: row.columns.map((col) => ({
              ...col,
              contents: col.contents.filter((content) => !isSurveyAnswerContent(content)),
            })),
          };
          return containedSurveyAnswers &&
            strippedRow.columns.every((column) => column.contents.length === 0)
            ? null
            : strippedRow;
        })
        .filter((row): row is NonNullable<typeof row> => row !== null),
    },
  };
}

function countSurveyAnswerBlocks(design: UnlayerDesign): number {
  return design.body.rows.reduce(
    (total, row) =>
      total +
      row.columns.reduce(
        (columnTotal, column) => columnTotal + column.contents.filter(isSurveyAnswerContent).length,
        0,
      ),
    0,
  );
}

export const designContainsSurveyAnswerBlock = (
  design: UnlayerDesign | null | undefined,
): boolean => !!design && countSurveyAnswerBlocks(design) > 0;

export function appendSurveyAnswerBlock(design: UnlayerDesign): UnlayerDesign {
  if (countSurveyAnswerBlocks(design) > 0) return design;
  const contentId = uuidv4();
  return {
    ...design,
    body: {
      ...design.body,
      rows: [
        ...design.body.rows,
        {
          id: uuidv4(),
          cells: [1],
          columns: [
            {
              id: uuidv4(),
              contents: [
                {
                  id: contentId,
                  type: "custom",
                  slug: SURVEY_ANSWERS_CONTENT_SLUG,
                  values: { _meta: { htmlID: contentId }, titleFontWeight: true },
                },
              ],
              values: {},
            },
          ],
          values: {},
        },
      ],
    },
  } as UnlayerDesign;
}

export function useUnlayerEditor(
  initialDesign: UnlayerDesign | null,
  {
    onPlainTextChange,
    isSurveyTrigger = false,
    insertSurveyBlockOnMount = false,
  }: {
    onPlainTextChange?: (text: string) => void;
    isSurveyTrigger?: boolean;
    insertSurveyBlockOnMount?: boolean;
  } = {},
): {
  editor: RefObject<EditorRef | null>;
  dirty: boolean;
  save: () => void;
  exportPdf: () => void;
  exportingPdf: boolean;
  onReady: UnlayerEmailEditorProps["onReady"];
  displayCondition: DisplayConditionReturn;
  reset: () => void;
} {
  const editor = useRef<EditorRef>(null);

  const [dirty, setDirty] = useState(false);

  const uploadImage = useUploadImage();
  const displayCondition = useDisplayCondition();
  const getImageAssets = useGetImageAssets();

  const methods = useFormContext<MomentFormMoment>();
  const { setValue, getValues } = methods;
  const surveyBlockCount = useRef(initialDesign ? countSurveyAnswerBlocks(initialDesign) : 0);
  const previousSurveyTrigger = useRef(insertSurveyBlockOnMount ? false : isSurveyTrigger);

  const onSave = useCallback(
    (rawHtml: string, design: UnlayerDesign, initialLoad?: boolean) => {
      // UnLayer double-encodes HTML entities in href attributes during export,
      // mangling quotes in survey JWT URLs (" -> &quot; -> &amp;quot;).
      // Decode them so the backend can parse the JSON payload.
      const triggerSurveyId = getValues("trigger_survey_id");
      const html = triggerSurveyId
        ? injectSurveyAnswerLoop(fixUnlayerHtmlEntities(rawHtml), triggerSurveyId)
        : fixUnlayerHtmlEntities(rawHtml);
      const designWithPolls = prepareUnlayerPollDesign(design, getValues("poll_ids") ?? []);
      setValue("email_moment.newDesign", false);
      setValue("email_moment.design", designWithPolls.design);
      setValue(
        "poll_ids",
        designWithPolls.polls.map(({ pollId }) => pollId),
      );
      setValue("email_moment.body", html ?? undefined, {
        shouldValidate: true,
        shouldDirty: !initialLoad,
      });
      // Filter out any dynamic segments that are no longer used (i.e they don't appear in the body)
      setValue(
        "dynamic_segment_id_map",
        omitBy(getValues("dynamic_segment_id_map") || {}, (segmentId) => {
          return !html?.includes(`{{#if ${segmentId}}}`);
        }),
      );
    },
    [getValues, setValue],
  );

  const exportPlainText = useCallback(
    (unlayer: NonNullable<EditorRef["editor"]>) => {
      if (!onPlainTextChange) return;

      (unlayer as Partial<UnlayerPlainTextEditor>).exportPlainText?.(({ text }) => {
        onPlainTextChange(text);
      });
    },
    [onPlainTextChange],
  );

  const save = useCallback(() => {
    editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
      onSave(html, emailDesign);
      setDirty(false);
    });
  }, [onSave]);

  const [exportingPdf, setExportingPdf] = useState(false);
  const { gaEvent } = useAnalytics();

  const exportPdf = useCallback(() => {
    const unlayerEditor = editor?.current?.editor;
    if (!unlayerEditor) return;

    unlayerEditor.setDisplayMode("document");
    setExportingPdf(true);

    unlayerEditor.exportPdf(({ url }) => {
      void (async () => {
        try {
          if (!url) throw new Error("No PDF URL returned");
          gaEvent("export_pdf", { event_category: "email" });
          await downloadFile(url, "email-export", "pdf");
        } catch (e) {
          console.error("PDF export failed", e);
          toast.error(
            "PDF export failed. Please try again or contact support if the issue persists.",
          );
        } finally {
          setExportingPdf(false);
          unlayerEditor.setDisplayMode("email");
        }
      })();
    });
  }, [gaEvent]);

  const onReady: UnlayerEmailEditorProps["onReady"] = useCallback(
    (unlayer: NonNullable<EditorRef["editor"]>) => {
      const addSurveyBlock = isSurveyTrigger && !previousSurveyTrigger.current;
      const removeSurveyBlock = !isSurveyTrigger && previousSurveyTrigger.current;
      previousSurveyTrigger.current = isSurveyTrigger;
      unlayer.registerCallback("displayCondition", displayCondition.onDisplayConditionRequest);

      if (uploadImage) unlayer.registerCallback("image", uploadImage);

      // Strip survey_answers blocks when loading a design for a non-survey trigger
      let designToLoad = initialDesign
        ? getValues("trigger_survey_id")
          ? initialDesign
          : stripSurveyAnswerBlocks(initialDesign)
        : null;
      if (initialDesign && removeSurveyBlock && countSurveyAnswerBlocks(initialDesign) > 0) {
        gaEvent("survey_response_block_removed", { action: "trigger_changed" });
        surveyBlockCount.current = 0;
      }
      if (designToLoad && addSurveyBlock) {
        const previousCount = countSurveyAnswerBlocks(designToLoad);
        designToLoad = appendSurveyAnswerBlock(designToLoad);
        surveyBlockCount.current = countSurveyAnswerBlocks(designToLoad);
        if (previousCount === 0) {
          gaEvent("survey_response_block_added", { action: "automatic" });
        }
      }
      if (designToLoad) unlayer.loadDesign(designToLoad);

      editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
        onSave(html, emailDesign, true);
        setDirty(false);
      });
      exportPlainText(unlayer);

      unlayer.addEventListener("design:updated", () => {
        setDirty(true);
        editor?.current?.editor?.exportHtml(({ html, design: emailDesign }) => {
          const nextCount = countSurveyAnswerBlocks(emailDesign);
          if (nextCount > surveyBlockCount.current) {
            gaEvent("survey_response_block_added", { action: "manual" });
          } else if (nextCount < surveyBlockCount.current) {
            gaEvent("survey_response_block_removed", { action: "manual" });
          }
          surveyBlockCount.current = nextCount;
          onSave(html, emailDesign);
          setDirty(false);
        });
        exportPlainText(unlayer);
      });

      unlayer.registerProvider(
        "userUploads",
        function (
          params: { page: number; searchText: string },
          done: (
            images: UnlayerUpload[],
            pagination: { hasMore: boolean; total: number; page: number; perPage: number },
          ) => void,
        ) {
          const search = params.searchText.trim().toLowerCase();

          void getImageAssets().then((contentLibraryAssets) => {
            const images = contentLibraryAssets
              ?.filter(
                (x) =>
                  !search ||
                  x.title.trim().toLowerCase().includes(search) ||
                  x.content_type?.trim().toLowerCase().includes(search),
              )
              .map((asset) => ({
                id: asset.id,
                location: asset.public_url,
                contentType: asset.mime_type,
                source: "user",
              })) as UnlayerUpload[];

            const page = params.page || 1;
            const perPage = 20;
            const total = images.length;
            const hasMore = total > page * perPage;

            done(images.slice((page - 1) * perPage, page * perPage), {
              hasMore,
              total,
              page,
              perPage,
            });
          });
        },
      );
    },
    [
      displayCondition,
      initialDesign,
      uploadImage,
      onSave,
      exportPlainText,
      getImageAssets,
      getValues,
      isSurveyTrigger,
      gaEvent,
    ],
  );

  return {
    editor,
    dirty,
    save,
    exportPdf,
    exportingPdf,
    onReady,
    displayCondition,
    reset: () => {
      initialDesign && editor?.current?.editor?.loadDesign(initialDesign);
      setDirty(false);
    },
  };
}
